import { useState, useEffect, useCallback, useContext } from 'react';
import { message as antdMessage } from 'antd';
import notificationService from '../services/notificationService';
import { UserContext } from '../context/UserContext';

/**
 * Custom hook for managing web push notifications
 * 
 * @returns {Object} Notification state and control functions
 */
const useNotifications = () => {
  const { user } = useContext(UserContext);
  const [permission, setPermission] = useState(notificationService.getPermissionStatus());
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(notificationService.isSupported);
  const [notification, setNotification] = useState(null);

  // Initialize notification listener
  useEffect(() => {
    if (!isSupported) return;

    console.log('🔔 Setting up foreground notification listener...');

    // Listen for foreground messages
    const unsubscribe = notificationService.onMessageListener()
      .then((payload) => {
        console.log('🎉 Received foreground notification:', payload);
        console.log('📱 Full payload:', JSON.stringify(payload, null, 2));
        console.log('📱 Notification object:', payload?.notification);
        console.log('📱 Data object:', payload?.data);
        
        setNotification(payload);
        
        // Firebase can send notification in two ways:
        // 1. In the notification field (when sent with notification object)
        // 2. In the data field (when sent as data-only message)
        
        const notifData = payload?.notification;
        const dataPayload = payload?.data;
        
        if (notifData && (notifData.title || notifData.body)) {
          // Standard notification field
          console.log('✅ Showing notification from notification field');
          antdMessage.destroy(); // Clear any existing messages first
          setTimeout(() => {
            antdMessage.info({
              key: `notif-${Date.now()}-${Math.random()}`, // Unique key for each notification
              content: (
                <div onClick={() => handleNotificationClick(payload)}>
                  <strong>{notifData.title}</strong>
                  <div>{notifData.body}</div>
                </div>
              ),
              duration: 10,
              style: { cursor: 'pointer', fontSize: '16px' }
            });
          }, 100); // Small delay to ensure each message shows
        } else if (dataPayload) {
          // Data-only message - create notification from data
          console.log('✅ Showing notification from data field');
          const title = dataPayload.title || 'New Message';
          const body = dataPayload.body || dataPayload.text || 'You have a new notification';
          
          antdMessage.destroy(); // Clear any existing messages first
          setTimeout(() => {
            antdMessage.info({
              key: `notif-${Date.now()}-${Math.random()}`, // Unique key for each notification
              content: (
                <div onClick={() => handleNotificationClick(payload)}>
                  <strong>{title}</strong>
                  <div>{body}</div>
                </div>
              ),
              duration: 10,
              style: { cursor: 'pointer', fontSize: '16px' }
            });
          }, 100); // Small delay to ensure each message shows
        } else {
          console.log('⚠️ No notification data found in payload');
        }
      })
      .catch((err) => console.log('❌ Failed to receive foreground message:', err));

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [isSupported]);

  // Handle notification click
  const handleNotificationClick = (payload) => {
    const url = payload?.notification?.click_action || payload?.data?.url;
    if (url) {
      window.location.href = url;
    }
  };

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      antdMessage.warning('Notifications are not supported in this browser');
      return false;
    }

    try {
      setLoading(true);
      const granted = await notificationService.requestPermission();
      setPermission(granted ? 'granted' : 'denied');
      
      if (granted) {
        antdMessage.success('Notification permission granted!');
        return true;
      } else {
        antdMessage.error('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      antdMessage.error('Failed to request notification permission');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Get and save FCM token
  const registerForNotifications = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Notifications not supported');
    }

    if (!user?._id) {
      throw new Error('User not logged in');
    }

    try {
      setLoading(true);
      
      // Get FCM token
      const fcmToken = await notificationService.getFCMToken();
      
      if (fcmToken) {
        setToken(fcmToken);
        
        // Save to backend
        await notificationService.saveTokenToServer(user._id, fcmToken);
        
        // Save to localStorage for persistence
        localStorage.setItem('fcm_token', fcmToken);
        
        antdMessage.success('Successfully registered for notifications!');
        return fcmToken;
      } else {
        throw new Error('Failed to get FCM token');
      }
    } catch (error) {
      console.error('Error registering for notifications:', error);
      antdMessage.error('Failed to register for notifications');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isSupported, user]);

  // Unregister from notifications (useful on logout)
  const unregisterFromNotifications = useCallback(async () => {
    try {
      const savedToken = token || localStorage.getItem('fcm_token');
      if (savedToken) {
        await notificationService.deleteTokenFromServer(savedToken);
        localStorage.removeItem('fcm_token');
        setToken(null);
        antdMessage.success('Unregistered from notifications');
      }
    } catch (error) {
      console.error('Error unregistering:', error);
    }
  }, [token]);

  // Show a test notification
  const showTestNotification = useCallback(() => {
    if (permission !== 'granted') {
      antdMessage.warning('Please grant notification permission first');
      return;
    }

    notificationService.showLocalNotification(
      'Test Notification',
      {
        body: 'This is a test notification from Edge Pages!',
        icon: '/logo192.png',
        tag: 'test-notification',
        data: { url: window.location.origin }
      }
    );
  }, [permission]);

  // Auto-register if user is logged in and permission is granted
  // This handles cases where user grants permission but wasn't registered yet
  useEffect(() => {
    const autoRegister = async () => {
      if (
        isSupported &&
        user?._id &&
        permission === 'granted' &&
        !token &&
        !loading
      ) {
        // Check if we already have a token in localStorage
        const savedToken = localStorage.getItem('fcm_token');
        if (savedToken) {
          setToken(savedToken);
          console.log('✅ Loaded FCM token from localStorage');
          return;
        }

        // Otherwise, register for new token silently (no success message)
        try {
          const fcmToken = await notificationService.getFCMToken();
          if (fcmToken) {
            setToken(fcmToken);
            await notificationService.saveTokenToServer(user._id, fcmToken);
            localStorage.setItem('fcm_token', fcmToken);
            console.log('✅ FCM token registered silently');
          }
        } catch (error) {
          console.error('Auto-register failed:', error);
        }
      }
    };

    autoRegister();
  }, [isSupported, user, permission, token, loading]);

  return {
    // State
    permission,
    token,
    loading,
    isSupported,
    notification,
    
    // Actions
    requestPermission,
    registerForNotifications,
    unregisterFromNotifications,
    showTestNotification
  };
};

export default useNotifications;
